/**
 * Module summary:
 * This page collects account registration details, validates the required
 * fields, and creates a new user account before routing the user into MFA
 * setup for first-time security enrollment.
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../atoms/Card';
import { Input } from '../atoms/Input';
import { Button } from '../atoms/Button';
import { useAuth } from '../../hooks/useAuth';
import {
  INPUT_LIMITS,
  assert,
  isStrongPassword,
  isValidEmail,
  normalizeEmail,
  normalizeUsPhoneE164,
  sanitizeSingleLineText,
} from '../../utils/inputSecurity';
import './auth-pages.css';

const US_STATES = [
  // Keep the state abbreviations in one shared list so the address form stays consistent and easy to maintain.
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY',
  'LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH',
  'OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'
];

export const CreateAccount: React.FC = () => {
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    organization: '',
    organizationStreetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const { register, loading } = useAuth();
  const navigate = useNavigate();

  const update = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    // Update only the targeted form field while preserving the rest of the draft registration data.
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const fullName = sanitizeSingleLineText(form.fullName, INPUT_LIMITS.displayName);
    const email = normalizeEmail(form.email);
    const organization = sanitizeSingleLineText(form.organization, INPUT_LIMITS.organization);
    const streetAddress = sanitizeSingleLineText(form.organizationStreetAddress, INPUT_LIMITS.address);
    const city = sanitizeSingleLineText(form.city, INPUT_LIMITS.city);
    const state = sanitizeSingleLineText(form.state, 2).toUpperCase();
    const zipCode = sanitizeSingleLineText(form.zipCode, 10);
    const phoneWithCode = normalizeUsPhoneE164(form.phone);

    if (!fullName || !email || !form.phone.trim() || !form.password) {
      setError('Full name, email, phone number, and password are required.');
      return;
    }
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!phoneWithCode) {
      // Require a 10-digit US number because the current MFA enrollment flow assumes +1 formatting.
      setError('Please enter a valid 10-digit phone number.');
      return;
    }
    if (!isStrongPassword(form.password)) {
      setError('Password must be at least 8 characters with one uppercase letter and one number.');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      assert(!state || US_STATES.includes(state), 'Please select a valid US state.');
      assert(!zipCode || /^\d{5}(?:-\d{4})?$/.test(zipCode), 'Please enter a valid ZIP code.');
      await register(fullName, email, form.password, {
        phone: phoneWithCode,
        organization,
        // Join the optional address pieces into one display string used in the user profile.
        organizationAddress: [streetAddress, city, state, zipCode].filter(Boolean).join(', '),
      });
      // Send newly registered users to a dedicated email-verification screen before MFA setup.
      navigate('/verify-email');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create account');
    }
  };

  return (
    <div className="auth-page">
      <Card className="auth-card auth-card--wide" padding="lg" shadow="lg">
        <div className="create-account__header">
          <img src="/Transparent-Logo.png" alt="Macha Group" className="create-account__logo" />
          <h2 className="create-account__title">Create Your Account</h2>
          <p className="create-account__subtitle">Set up your organization profile to start security assessments.</p>
        </div>

        {error && <div className="create-account__error">{error}</div>}

        <form onSubmit={onSubmit} className="create-account__form">
          {/* Personal Information */}
          <fieldset className="create-account__fieldset">
            <legend className="create-account__legend">Personal Information</legend>
            <div className="create-account__row">
              <Input label="Full Name" value={form.fullName} onChange={update('fullName')} placeholder="John Doe" required fullWidth />
              <Input label="Email Address" type="email" value={form.email} onChange={update('email')} placeholder="john@example.com" required fullWidth />
            </div>
            <div className="create-account__row">
              <Input label="Phone Number" value={form.phone} onChange={update('phone')} placeholder="(555) 123-4567" helperText="US number format only" required fullWidth />
              <Input label="Organization" value={form.organization} onChange={update('organization')} placeholder="School District / Company" fullWidth />
            </div>
          </fieldset>

          {/* Address */}
          <fieldset className="create-account__fieldset">
            <legend className="create-account__legend">Organization Address (Optional)</legend>
            <Input label="Street Address" value={form.organizationStreetAddress} onChange={update('organizationStreetAddress')} placeholder="123 Main Street" fullWidth />
            <div className="create-account__row create-account__row--3">
              <Input label="City" value={form.city} onChange={update('city')} placeholder="Springfield" fullWidth />
              <div className="create-account__select-wrapper">
                <label className="input__label" htmlFor="state-select">State</label>
                <select id="state-select" className="create-account__select" value={form.state} onChange={update('state')}>
                  <option value="">Select</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <Input label="Zip Code" value={form.zipCode} onChange={update('zipCode')} placeholder="62704" fullWidth />
            </div>
          </fieldset>

          {/* Security */}
          <fieldset className="create-account__fieldset">
            <legend className="create-account__legend">Security</legend>
            <div className="create-account__row">
              <Input label="Password" type="password" value={form.password} onChange={update('password')} placeholder="Min 8 chars, 1 upper, 1 number" required fullWidth />
              <Input label="Confirm Password" type="password" value={form.confirmPassword} onChange={update('confirmPassword')} placeholder="Re-enter password" required fullWidth />
            </div>
            <p className="create-account__hint">Password must be at least 8 characters with one uppercase letter and one number.</p>
          </fieldset>

          <Button type="submit" fullWidth loading={loading} size="lg">Create Account</Button>
        </form>

        <p className="auth-links" style={{ textAlign: 'center', marginTop: '1rem' }}>
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </Card>
    </div>
  );
};

export default CreateAccount;

